import React, { useMemo, useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import {
  FaCheckCircle,
  FaTimesCircle,
  FaFileInvoice,
  FaTruck,
  FaStar,
  FaSpinner,
  FaTools,
  FaClipboardCheck,
  FaMoneyBillWave,
  FaHandshake,
  FaDownload,
  FaRoute,
} from 'react-icons/fa';
import { jobCardService, bookingService } from '../../services/apiService';

const CORE_FLOW = [
  { id: 'Created', label: 'Booked', icon: FaFileInvoice },
  { id: 'GarageReview', label: 'Garage review', icon: FaClipboardCheck },
  { id: 'GarageAccepted', label: 'Accepted', icon: FaCheckCircle },
  { id: 'Intake', label: 'Intake', icon: FaClipboardCheck },
  { id: 'PendingCustomerApproval', label: 'Estimate', icon: FaClipboardCheck },
  { id: 'CustomerApproved', label: 'Approved', icon: FaHandshake },
  { id: 'InProgress', label: 'In progress', icon: FaTools },
  { id: 'Billed', label: 'Invoice', icon: FaMoneyBillWave },
  { id: 'Paid', label: 'Paid', icon: FaCheckCircle },
  { id: 'Delivered', label: 'Delivered', icon: FaTruck },
  { id: 'Closed', label: 'Closed', icon: FaCheckCircle },
];

const PRICE_INCREASE_THRESHOLD = 0.1;

const JobDetails = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const shareToken = searchParams.get('token');
  const { user } = useAuth();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState('');
  const [editedParts, setEditedParts] = useState(null);
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [pendingApprove, setPendingApprove] = useState(false);
  const [linkedBookingId, setLinkedBookingId] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const jc = shareToken
        ? await jobCardService.getByShareToken(jobId, shareToken)
        : await jobCardService.getById(jobId);
      setJob(jc);
    } catch (e) {
      setJob(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, shareToken]);

  useEffect(() => {
    if (!jobId || shareToken || !user) {
      setLinkedBookingId(null);
      return;
    }
    let cancelled = false;
    bookingService
      .getStatusByJobCard(jobId)
      .then((data) => {
        if (!cancelled && data?.bookingId) setLinkedBookingId(data.bookingId);
      })
      .catch(() => {
        if (!cancelled) setLinkedBookingId(null);
      });
    return () => {
      cancelled = true;
    };
  }, [jobId, shareToken, user]);

  const partsToShow = useMemo(() => {
    if (editedParts !== null) return editedParts;
    return job?.estimate?.parts || [];
  }, [job?.estimate?.parts, editedParts]);

  const totals = useMemo(() => {
    const laborItems = job?.estimate?.laborItems || [];
    const partsList = editedParts !== null ? editedParts : (job?.estimate?.parts || []);

    const laborSub = laborItems.reduce(
      (s, li) => s + (Number(li.hours) || 0) * (Number(li.rate) || 0),
      0
    );

    const partsSub = partsList.reduce((s, p) => {
      const qty = Number(p.quantity || 1);
      const unit = Number(p.unitPrice ?? 0);
      const basePrice = unit > 0 ? unit : Number(p.price ?? 0);
      return s + basePrice * qty;
    }, 0);

    const subtotal = laborSub + partsSub;
    const tax = subtotal * 0.18;
    const total = subtotal + tax;

    return { subtotal, tax, total };
  }, [job?.estimate?.laborItems, job?.estimate?.parts, editedParts]);

  const originalTotal = useMemo(() => Number(job?.estimate?.total || 0), [job?.estimate?.total]);

  const allocStatus = (job?.allocation?.status || 'pending').toString();

  const customerQcApprovedBySuperadmin =
    job?.qc?.approvedByRole === 'superadmin' ||
    (!job?.qc?.approvedByRole &&
      (job?.timeline || []).some((t) => t?.status === 'QCApproved' && t?.byRole === 'superadmin'));

  // Customer should only see QC stages after superadmin approves.
  // Until then, treat QC-related statuses as "InProgress" for display purposes.
  const displayStatus = useMemo(() => {
    const s = (job?.status || 'Created').toString();
    if (s === 'ReadyForQC' || s === 'QCRejected') return 'InProgress';
    if (s === 'QCApproved') return customerQcApprovedBySuperadmin ? 'Billed' : 'InProgress';
    return s;
  }, [job?.status, customerQcApprovedBySuperadmin]);

  const flowState = useMemo(() => {
    const s = displayStatus;
    const reached = new Set();
    reached.add('Created');
    reached.add('GarageReview'); // always after booking, garage reviews
    if (allocStatus === 'confirmed') reached.add('GarageAccepted');

    // Mark statuses reached based on job.status
    const statusOrder = [
      'Created',
      'Intake',
      'PendingCustomerApproval',
      'CustomerApproved',
      'PartsOrdered',
      'InProgress',
      'Billed',
      'Paid',
      'Delivered',
      'Closed'
    ];
    const upto = statusOrder.indexOf(s);
    if (upto >= 0) {
      statusOrder.slice(0, upto + 1).forEach((k) => reached.add(k));
    }

    const current =
      allocStatus === 'reallocate'
        ? 'GarageReview'
        : s === 'Created' && allocStatus === 'confirmed'
          ? 'GarageAccepted'
          : s === 'Created'
            ? 'GarageReview'
            : s;

    return { reached, current };
  }, [displayStatus, allocStatus]);

  if (!job) {
    return (
      <div className="checkout-light-page min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <p className="text-gray-600 mb-4">{loading ? 'Loading…' : 'Job not found'}</p>
          <button
            onClick={() => navigate('/service/booking')}
            className="px-6 py-3 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700"
          >
            Book New Service
          </button>
        </div>
      </div>
    );
  }

  const getStatusColor = (status) => {
    const colors = {
      Created: 'bg-slate-100 text-slate-800',
      Intake: 'bg-indigo-100 text-indigo-800',
      PendingCustomerApproval: 'bg-amber-100 text-amber-800',
      CustomerApproved: 'bg-emerald-100 text-emerald-800',
      PartsOrdered: 'bg-teal-100 text-teal-800',
      CustomerRejected: 'bg-red-100 text-red-800',
      InProgress: 'bg-blue-100 text-blue-800',
      ReadyForQC: 'bg-teal-100 text-teal-800',
      QCRejected: 'bg-orange-100 text-orange-900',
      QCApproved: 'bg-indigo-100 text-indigo-800',
      Billed: 'bg-purple-100 text-purple-800',
      Paid: 'bg-green-100 text-green-800',
      Delivered: 'bg-green-100 text-green-800',
      Closed: 'bg-gray-200 text-gray-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const updatePartQty = (idx, qty) => {
    const next = [...partsToShow];
    next[idx] = { ...next[idx], quantity: Math.max(1, parseInt(qty, 10) || 1) };
    setEditedParts(next);
  };

  const removePart = (idx) => {
    const p = partsToShow[idx];
    if (p?.mandatory) return;
    setEditedParts(partsToShow.filter((_, i) => i !== idx));
  };

  const addPart = () => {
    setEditedParts([...(editedParts ?? partsToShow), { name: 'New part', quantity: 1, price: 0, unitPrice: 0, mandatory: false, customerAdded: true }]);
  };

  const saveChanges = async () => {
    try {
      setActionLoading('save');
      const spareParts = (editedParts ?? partsToShow).map((p) => ({
        ...p,
        unitPrice: p.unitPrice ?? p.price,
        qty: p.quantity,
        mandatory: p.mandatory ?? false,
        customerAdded: p.customerAdded ?? false
      }));
      await jobCardService.patchItems(jobId, { spareParts, etag: job?.etag });
      setEditedParts(null);
      await refresh();
    } catch (e) {
      alert(e.message || 'Failed to save changes');
    } finally {
      setActionLoading('');
    }
  };

  const handleApprove = async (forceConsent = false) => {
    if (editedParts !== null && !forceConsent) {
      const newTotal = totals.total;
      if (originalTotal > 0 && newTotal > originalTotal * (1 + PRICE_INCREASE_THRESHOLD)) {
        setShowConsentModal(true);
        setPendingApprove(true);
        return;
      }
      await saveChanges();
    }
    try {
      setActionLoading('approve');
      await jobCardService.approve(jobId);
      await refresh();
    } catch (e) {
      alert(e.message || 'Failed to approve');
    } finally {
      setActionLoading('');
      setShowConsentModal(false);
      setPendingApprove(false);
    }
  };

  const confirmConsentApprove = async () => {
    setShowConsentModal(false);
    setPendingApprove(false);
    try {
      setActionLoading('approve');
      if (editedParts !== null) await saveChanges();
      await jobCardService.approve(jobId);
      await refresh();
    } catch (e) {
      alert(e.message || 'Failed to approve');
    } finally {
      setActionLoading('');
    }
  };

  const handleReject = async () => {
    try {
      setActionLoading('reject');
      await jobCardService.reject(jobId);
      await refresh();
    } catch (e) {
      alert(e.message || 'Failed to reject');
    } finally {
      setActionLoading('');
    }
  };

  const handleDownloadInvoice = async () => {
    try {
      setActionLoading('download');
      await jobCardService.downloadInvoice(jobId);
    } catch (e) {
      alert(e.message || 'Download failed');
    } finally {
      setActionLoading('');
    }
  };

  return (
    <div className="checkout-light-page min-h-screen bg-slate-50 py-8">
      <div className="section-container max-w-4xl mx-auto w-full">
        <div className="mb-6">
          <button
            onClick={() => navigate(-1)}
            className="text-red-600 hover:text-red-700 font-medium mb-4 inline-flex items-center gap-1"
          >
            ← Back
          </button>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
                Job #{(job.jobCardId || job._id || '').toString().slice(-8)}
              </h1>
              <p className="text-gray-600 mt-1">
                {job.vehicle?.make} {job.vehicle?.model} {job.vehicle?.variant}
              </p>
              <p className="text-sm text-gray-500 mt-0.5">
                Garage: {job.garageId?.garageDetails?.garageName || job.garageId?.name || '—'}
              </p>
            </div>
            <span
              className={`inline-flex px-4 py-2 rounded-full text-sm font-semibold shrink-0 ${getStatusColor(
                displayStatus
              )}`}
            >
              {(displayStatus || '').toString().replace(/([A-Z])/g, ' $1').trim()}
            </span>
          </div>
        </div>

        {(job?.allocation?.status || job?.garageIntake) && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
            {job?.allocation?.status === 'pending' && (
              <div className="text-sm text-gray-700">
                <span className="font-semibold">Garage review:</span> Awaiting garage decision (accept or reallocate).
              </div>
            )}
            {job?.allocation?.status === 'reallocate' && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                <div className="font-bold">Reallocated by garage</div>
                <div className="mt-1">
                  <span className="font-semibold">Reason:</span> {job?.allocation?.reason || '—'}
                </div>
              </div>
            )}

            {(job.status === 'Intake' || job?.garageIntake?.scheduledTime || job?.garageIntake?.assignedMechanicId) && (
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-500 font-semibold">Scheduled intake</div>
                  <div className="font-semibold text-gray-900">
                    {job?.garageIntake?.scheduledTime ? new Date(job.garageIntake.scheduledTime).toLocaleString() : '—'}
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-500 font-semibold">Service mode</div>
                  <div className="font-semibold text-gray-900">{job?.garageIntake?.serviceMode || '—'}</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-500 font-semibold">Assigned mechanic</div>
                  <div className="font-semibold text-gray-900">{job?.garageIntake?.assignedMechanicId || '—'}</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-500 font-semibold">Preliminary diagnosis</div>
                  <div className="text-gray-900">{job?.garageIntake?.preliminaryNotes || '—'}</div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Status Flow Progress (aligned to current workflow) */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Job Lifecycle</h2>
          <div className="overflow-x-auto pb-2">
            <div className="flex gap-2 min-w-max">
              {CORE_FLOW.map((step) => {
                const isReached = flowState.reached.has(step.id);
                const isCurrent = flowState.current === step.id;
                const Icon = step.icon;
                const label =
                  step.id === 'GarageReview' && allocStatus === 'reallocate'
                    ? 'Reallocated'
                    : step.label;
                return (
                  <div
                    key={step.id}
                    className={`flex flex-col items-center min-w-[80px] p-2 rounded-lg ${
                      isCurrent ? 'bg-red-50 ring-2 ring-red-500' : isReached ? 'bg-gray-50' : 'bg-gray-50/50 opacity-60'
                    }`}
                  >
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        isReached ? 'bg-red-600 text-white' : 'bg-gray-200 text-gray-500'
                      }`}
                    >
                      <Icon className="text-sm" />
                    </div>
                    <span className="text-xs font-medium text-gray-700 mt-1 text-center leading-tight">
                      {label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {/* Timeline */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Timeline</h2>
              <div className="space-y-4">
                {(job.timeline || []).length > 0 ? (
                  (job.timeline || []).map((milestone, idx) => {
                    const timelineStatus = String(milestone?.status || '').trim();
                    // QC is internal: hide all QC timeline items from the customer.
                    if (['ReadyForQC', 'QCRejected', 'QCApproved'].includes(timelineStatus)) return null;

                    return (
                      <div key={idx} className="flex items-start gap-4">
                        <div className="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center text-white flex-shrink-0">
                          <FaCheckCircle className="text-sm" />
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-gray-900">
                            {timelineStatus.replace(/([A-Z])/g, ' $1').trim()}
                          </p>
                          <p className="text-sm text-gray-600">
                            {new Date(milestone.at || Date.now()).toLocaleString()}
                          </p>
                          {milestone.note && (
                            <p className="text-xs text-gray-500 mt-0.5">{milestone.note}</p>
                          )}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <p className="text-sm text-gray-500">No timeline yet. Garage will update as work progresses.</p>
                )}
              </div>
            </div>

            {/* STEP 4: Customer Approval - PendingCustomerApproval */}
            {job.status === 'PendingCustomerApproval' && (
              !user && shareToken ? (
              <div className="bg-white rounded-xl shadow-sm border border-amber-200 p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Service Estimate – Review Required</h2>
                <div className="space-y-2 mb-4">
                  {(job.estimate?.parts || []).map((part, idx) => (
                    <div key={idx} className="flex justify-between p-2 bg-gray-50 rounded">
                      <span>{part.name || 'Part'}</span>
                      <span>₹{Number((part.unitPrice ?? part.price) || 0) * Number(part.quantity || 1)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between font-bold pt-2">Total: ₹{totals.total.toFixed(2)}</div>
                </div>
                <p className="text-gray-600 mb-4">
                  Please log in with your customer account to approve or reject this estimate.
                </p>
                <button
                  onClick={() => navigate('/login', { state: { returnTo: window.location.pathname + window.location.search } })}
                  className="py-3 px-4 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700"
                >
                  Log in to Approve
                </button>
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-sm border border-amber-200 p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Service Estimate – Your Approval Required</h2>
                <p className="text-sm text-gray-600 mb-4">
                  The garage has submitted an estimate. You can remove optional parts, adjust quantities, or add parts. Mandatory items cannot be removed.
                </p>
                <div className="space-y-4">
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Parts & Labour</h3>
                    <div className="space-y-2">
                      {(job.estimate?.laborItems || []).map((li, idx) => (
                        <div key={`lab-${idx}`} className="flex justify-between p-3 bg-gray-50 rounded-lg">
                          <span className="text-gray-700">{li.description || 'Labour'}</span>
                          <span className="font-semibold">₹{Number(li.hours || 0) * Number(li.rate || 0)}</span>
                        </div>
                      ))}
                      {partsToShow.map((part, idx) => {
                        const qty = Number(part.quantity || 1);
                        const unit = Number(part.unitPrice ?? 0);
                        const basePrice = unit > 0 ? unit : Number(part.price ?? 0);
                        const lineTotal = basePrice * qty;
                        return (
                          <div key={idx} className="flex items-center justify-between gap-2 p-3 bg-gray-50 rounded-lg">
                            <div className="flex-1 min-w-0">
                              <span className="text-gray-700">{part.name || 'Part'}</span>
                              {part.mandatory && <span className="ml-2 text-xs text-amber-600">(Mandatory)</span>}
                            </div>
                            {editedParts !== null ? (
                              <>
                                <input
                                  type="number"
                                  min={1}
                                  value={qty}
                                  onChange={(e) => updatePartQty(idx, e.target.value)}
                                  className="w-16 px-2 py-1 border rounded text-sm"
                                />
                                <span className="font-semibold w-20 text-right">
                                  ₹{lineTotal}
                                </span>
                                {!part.mandatory && (
                                  <button
                                    type="button"
                                    onClick={() => removePart(idx)}
                                    className="text-red-600 hover:text-red-700 text-sm"
                                  >
                                    Remove
                                  </button>
                                )}
                              </>
                            ) : (
                              <span className="font-semibold">
                                ₹{lineTotal}
                              </span>
                            )}
                          </div>
                        );
                      })}
                      {partsToShow.length === 0 && (
                        <p className="text-sm text-gray-500">No parts added.</p>
                      )}
                      {editedParts !== null && (
                        <button
                          type="button"
                          onClick={addPart}
                          className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                        >
                          + Add part
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="border-t pt-4 space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Subtotal</span>
                      <span className="font-semibold">₹{totals.subtotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Tax (18%)</span>
                      <span className="font-semibold">₹{totals.tax.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-xl font-bold text-red-600 pt-2 border-t">
                      <span>Total</span>
                      <span>₹{totals.total.toFixed(2)}</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 pt-2">
                    {editedParts === null ? (
                      <button
                        type="button"
                        onClick={() => setEditedParts(partsToShow.map((p) => ({ ...p })))}
                        className="py-2 px-4 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50"
                      >
                        Edit items
                      </button>
                    ) : (
                      <>
                        <button
                          onClick={saveChanges}
                          disabled={actionLoading === 'save'}
                          className="py-2 px-4 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
                        >
                          {actionLoading === 'save' ? 'Saving…' : 'Save changes'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setEditedParts(null)}
                          className="py-2 px-4 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50"
                        >
                          Cancel edit
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => handleApprove()}
                      disabled={actionLoading === 'approve'}
                      className="py-3 px-4 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700 disabled:opacity-50"
                    >
                      {actionLoading === 'approve' ? 'Approving…' : 'Approve Estimate'}
                    </button>
                    <button
                      onClick={handleReject}
                      disabled={actionLoading === 'reject'}
                      className="py-3 px-4 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 disabled:opacity-50"
                    >
                      {actionLoading === 'reject' ? 'Rejecting…' : 'Reject'}
                    </button>
                  </div>
                </div>
              </div>
            )
            )}

            {showConsentModal && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Price Increase Confirmation</h3>
                  <p className="text-gray-600 mb-4">
                    The total has increased by more than {(PRICE_INCREASE_THRESHOLD * 100).toFixed(0)}%. Original: ₹{originalTotal.toFixed(2)} → New: ₹{totals.total.toFixed(2)}. Do you approve this change?
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={confirmConsentApprove}
                      className="flex-1 py-2 px-4 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700"
                    >
                      Yes, Approve
                    </button>
                    <button
                      onClick={() => { setShowConsentModal(false); setPendingApprove(false); }}
                      className="flex-1 py-2 px-4 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* STEP 8: Payment - Billed */}
            {(job.status === 'Billed' ||
              job.status === 'Paid' ||
              job.status === 'Delivered' ||
              job.status === 'Closed') && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Invoice</h2>
                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Invoice No</span>
                    <span className="font-semibold">{job.invoice?.invoiceNo || '—'}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Amount</span>
                    <span className="font-semibold">₹{job.invoice?.total || totals.total}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Status</span>
                    <span className="font-semibold">{job.status}</span>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-2 mt-4">
                  {(job.status === 'Billed' || job.status === 'Paid' || job.status === 'Delivered' || job.status === 'Closed') && (
                    <button
                      onClick={handleDownloadInvoice}
                      disabled={actionLoading === 'download'}
                      className="flex-1 flex items-center justify-center gap-2 py-3 px-4 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 disabled:opacity-50"
                    >
                      {actionLoading === 'download' && <FaSpinner className="animate-spin" />}
                      <FaDownload /> Download PDF
                    </button>
                  )}
                  {job.status === 'Billed' && (
                    <button
                      onClick={() => navigate(`/service/job/${jobId}/payment`)}
                      className="flex-1 py-3 px-4 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 flex items-center justify-center gap-2"
                    >
                      Pay Now
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Status-specific messages for other stages */}
            {['Created', 'CustomerApproved', 'PartsOrdered', 'InProgress', 'Billed'].includes(
              displayStatus
            ) && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <p className="text-gray-700">
                  {displayStatus === 'Created' &&
                    'Your service request has been sent to the garage. They will inspect the vehicle and send an estimate soon.'}
                  {displayStatus === 'CustomerApproved' &&
                    'You have approved the estimate. The garage will now begin the repair work.'}
                  {displayStatus === 'PartsOrdered' &&
                    'Parts order has been placed. The garage will start repair once parts arrive.'}
                  {displayStatus === 'InProgress' &&
                    'Your vehicle is being serviced. The garage will update you when the work is complete.'}
                  {displayStatus === 'Billed' &&
                    'Final review approved. Invoice will be generated shortly.'}
                </p>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-4">
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-4">Job Information</h3>
                <div className="space-y-3">
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase">Service Mode</p>
                    <p className="font-semibold">Garage Service</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase">Complaint</p>
                    <p className="font-semibold">{job.complaintDescription || 'Not specified'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase">Preferred Slot</p>
                    <p className="font-semibold">
                      {job.preferredServiceDate
                        ? new Date(job.preferredServiceDate).toLocaleString()
                        : 'Not specified'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase">Created</p>
                    <p className="font-semibold">{new Date(job.createdAt).toLocaleString()}</p>
                  </div>
                </div>
              </div>

              {Array.isArray(job.customerPhotos) && job.customerPhotos.length > 0 && (
                <div className="pt-3 border-t border-gray-100">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                    Your uploaded photos
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {job.customerPhotos.map((url, idx) => (
                      <a
                        key={url + idx}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block w-20 h-20 rounded-lg overflow-hidden border border-gray-200 bg-gray-50 hover:ring-2 hover:ring-red-500 transition"
                      >
                        <img
                          src={url}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Quick Actions</h3>
              <div className="space-y-2">
                {linkedBookingId && (
                  <button
                    type="button"
                    onClick={() => navigate(`/service/booking/status/${linkedBookingId}`)}
                    className="w-full flex items-center gap-2 py-2.5 px-4 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700"
                  >
                    <FaRoute /> Track booking progress
                  </button>
                )}
                <button
                  onClick={() => navigate(`/service/job/${jobId}/jobcard`)}
                  className={`w-full flex items-center gap-2 py-2.5 px-4 font-semibold rounded-lg ${
                    linkedBookingId
                      ? 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                      : 'bg-red-600 text-white hover:bg-red-700'
                  }`}
                >
                  <FaFileInvoice /> View Job Card
                </button>
                <button
                  onClick={() => navigate('/service/booking')}
                  className="w-full flex items-center gap-2 py-2.5 px-4 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50"
                >
                  <FaTruck /> Book New Service
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobDetails;
